import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ModalRef, ModalService } from '@epicuro-next/ataraxia/modal';
import {
  confirmActiveAccountSelection,
  loadAccounts,
  selectAccounts,
  selectAccountsLoaded,
} from '@epicuro-next/state/accounts-state';
import { setActiveBasketAccounts } from '@epicuro-next/state/basket-state';
import { go } from '@epicuro-next/state/router-store';
import { select, Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { filter, map, take, takeUntil, withLatestFrom } from 'rxjs/operators';

import { AccountSelectorModalComponent } from '../account-selector-modal/account-selector-modal.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'epc-select-accounts-page',
  templateUrl: './select-accounts-page.component.html',
  styleUrls: ['./select-accounts-page.component.scss'],
  standalone: false,
})
export class SelectAccountsPageComponent implements OnInit, OnDestroy {
  /** Select loaded accounts state */
  public isLoaded$: Observable<boolean> = this.store.pipe(
    select(selectAccountsLoaded),
  );

  private modalRef: ModalRef;
  private destroy$$ = new Subject<void>();

  /** Dependencies */
  constructor(private store: Store, private modalService: ModalService) {}

  /** Angular's OnInit hook */
  public ngOnInit() {
    this.store.dispatch(loadAccounts());
    this.resolveDefaultAccount();
  }

  public ngOnDestroy() {
    this.destroy$$.next();
    this.destroy$$.complete();
    this.modalRef?.dismiss();
  }

  /**
   * method to wait accounts are loaded
   */
  private waitForAccountsLoaded() {
    return this.store.pipe(
      select(selectAccountsLoaded),
      filter(Boolean),
      take(1),
    );
  }

  /**
   * method to resolve default account
   */
  private resolveDefaultAccount() {
    this.waitForAccountsLoaded()
      .pipe(
        take(1),
        withLatestFrom(this.store.pipe(select(selectAccounts))),
        map(([_, accounts]) => {
          // initial loading
          const isOnlyDefaultAccount =
            accounts.length === 1 && accounts[0].shipToAddresses.length === 1;

          if (isOnlyDefaultAccount) {
            const basketAccounts = accounts.map((account) => {
              return {
                ...account,
                shipToAddresses: account.shipToAddresses.map((address) => ({
                  ...address,
                  hasDeliveryItems: true,
                })),
              };
            });

            this.store.dispatch(setActiveBasketAccounts({ basketAccounts }));
            this.store.dispatch(
              confirmActiveAccountSelection({
                activeAccount: basketAccounts[0],
              }),
            );
            this.store.dispatch(go({ commands: ['/'] }));
          } else {
            this.openModal();
          }
        }),
        takeUntil(this.destroy$$),
      )
      .subscribe();
  }

  private openModal() {
    this.modalRef = this.modalService.open(AccountSelectorModalComponent, {
      backdrop: 'static',
      scrollable: true,
    });
  }
}
